/*       */

import { isEmpty, isDefined } from 'app/utils/utils';
import validate from 'app/utils/validator/validate';
import { formatDate } from 'app/utils/date/date';
import { bind, memoize } from 'app/utils/decorators/decoratorUtils';
import { prepareFunction, enrichContext } from 'app/utils/designer/form/formUtils';
import worker from 'app/utils/worker/worker';
import { get, pick } from 'app/utils/lo/lo';
import affectliSso from 'app/auth/affectliSso';
import { set } from 'app/utils/immutable/Immutable';

export default class FormValidator {
    errors;
    constraints = {};
    components = {};
    dynamicDefinitions = {};
    customValidations = {};
    customConstraints = {};

    constructor(formComponents) {
        this.components = {};
        this.customConstraints = {};
        this.dynamicDefinitions = {};

        const constraints = !Array.isArray(formComponents)
            ? {}
            : formComponents.reduce((accum, component) => {
                const { properties, constraints } = component;
                const { name } = properties || {};
                if (!name || isEmpty(constraints)) {
                    return accum;
                }
                this.components[name] = component;
                const { dynamicDefinitions, ...enhanced } = this._enhanceConstraint(component);

                if (dynamicDefinitions) {
                    this.dynamicDefinitions[name] = dynamicDefinitions;
                }
                if (!isEmpty(enhanced)) {
                    accum[name] = enhanced;
                }
                return accum;

            }, {});
            
        this.constraints = this._normalizeContraints(constraints);
    }

    @bind
    _enhanceConstraint(component) {
        const { type, constraints, properties } = component;
        const parsed = { ...constraints };
        if (component?.properties?.calculation) {
            return parsed;
        }
        // TODO: enhance constraints adding type:
        if (type) {
            switch (type) {
                case 'text':
                    parsed.type = 'string';
                    break;
                case 'number':
                case 'boolean':
                    parsed.type = type;
                    break;
                case 'hyperlink': {
                    if (properties.hideTickIcon) {
                        parsed.custom = (data) => {
                            const { link, text } = data?.[properties?.name] || {};
                            if (!link || !text) {
                                return {
                                    format: {
                                        pattern: /somefaketest/,
                                        message: 'Both text and link are required.',
                                    },
                                };
                            }
                        }
                    }
                    break;
                }
                default:
            }
        }
        return parsed;
    }

    _normalizeContraints(constraintDefinitions) {
        if (isEmpty(constraintDefinitions)) {
            return {};
        }
        const normalized = {};
        Object.entries(constraintDefinitions).forEach(([name, constraints]) => {
            if (normalized[name]) {
                console.warn('more than one component is adding constraints to the same property:', name); // eslint-disable-line no-console
            }
            normalized[name] = normalized[name] || {};
            // $FlowFixMe
            Object.entries(constraints).forEach(([constraint, value]) => {
                switch (constraint) {
                    case 'required':
                        if (value) {
                            normalized[name].presence = { allowEmpty: false, message: '{label} is required.' };
                        }
                        break;
                    case 'minLength': {
                        const length = normalized[name].length || {};
                        length.minimum = value;
                        length.tooShort = 'Minimum %{count} letters or more.';
                        normalized[name].length = length;
                        break;
                    }
                    case 'maxLength': {
                        const length = normalized[name].length || {};
                        length.maximum = value;
                        length.tooLong = 'Maximum %{count} letters or less.';
                        normalized[name].length = length;
                        break;
                    }
                    case 'numericality':
                        normalized[name].numericality = value;
                        break;
                    case 'min':
                        normalized[name].numericality = {
                            greaterThanOrEqualTo: value,
                            ...get(normalized[name], 'numericality', {}),
                        };
                        break;
                    case 'max':
                        normalized[name].numericality = {
                            lessThanOrEqualTo: value,
                            ...get(normalized[name], 'numericality', {}),
                        };
                        break;
                    case 'onlyInteger':
                        normalized[name].numericality = {
                            onlyInteger: value,
                            ...get(normalized[name], 'numericality', {}),
                        };
                        break;
                    case 'datetime':
                        if (value && typeof value === 'object' && value.latest) {
                            // $FlowFixMe
                            value.message = () => `Must be no later than ${formatDate(value.latest.toDate())}`;
                        }
                        if (value && typeof value === 'object' && value.earliest) {
                            // $FlowFixMe
                            value.message = () => `Must be no earlier than  ${formatDate(value.earliest.toDate())}`;
                        }
                        normalized[name].datetime = value;
                        break;
                    case 'email':
                        normalized[name].email = { message: '{label} is not a valid email.' };
                        break;
                    case 'noWhiteSpace':
                        normalized[name].format = { pattern: /^\S*$/, message: '{label} can\'t contain space(s).' };
                        break;
                    case 'noWhiteSpaceDots':
                        normalized[name].format = { pattern: /^([^.|^ ])*$/, message: '{label} can\'t contain dots(s) or space(s).' };
                        break;
                    case 'exclusion':
                        normalized[name].exclusion = value;
                        break;
                    case 'uuid':
                        normalized[name].uuid = value;
                        break;
                    case 'uri':
                        normalized[name].format = {
                            pattern: /^(?:([a-z][a-z0-9+-\.]*:\/\/)?(?:(?:(?:[a-z0-9-\._~%0-9a-f!\$&'\(\)\*\+,;=:]*@)?(?:\[(?:(?:(?:[0-9a-f]{1,4}:){6}(?:[0-9a-f]{1,4}:[0-9a-f]{1,4}|(?:(?:0?0?[0-9]|0?[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])\.){3}(?:0?0?[0-9]|0?[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5]))|::(?:[0-9a-f]{1,4}:){5}(?:[0-9a-f]{1,4}:[0-9a-f]{1,4}|(?:(?:0?0?[0-9]|0?[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])\.){3}(?:0?0?[0-9]|0?[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5]))|(?:[0-9a-f]{1,4})?::(?:[0-9a-f]{1,4}:){4}(?:[0-9a-f]{1,4}:[0-9a-f]{1,4}|(?:(?:0?0?[0-9]|0?[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])\.){3}(?:0?0?[0-9]|0?[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5]))|(?:(?:[0-9a-f]{1,4}:){0,1}[0-9a-f]{1,4})?::(?:[0-9a-f]{1,4}:){3}(?:[0-9a-f]{1,4}:[0-9a-f]{1,4}|(?:(?:0?0?[0-9]|0?[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])\.){3}(?:0?0?[0-9]|0?[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5]))|(?:(?:[0-9a-f]{1,4}:){0,2}[0-9a-f]{1,4})?::(?:[0-9a-f]{1,4}:){2}(?:[0-9a-f]{1,4}:[0-9a-f]{1,4}|(?:(?:0?0?[0-9]|0?[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])\.){3}(?:0?0?[0-9]|0?[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5]))|(?:(?:[0-9a-f]{1,4}:){0,3}[0-9a-f]{1,4})?::[0-9a-f]{1,4}:(?:[0-9a-f]{1,4}:[0-9a-f]{1,4}|(?:(?:0?0?[0-9]|0?[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])\.){3}(?:0?0?[0-9]|0?[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5]))|(?:(?:[0-9a-f]{1,4}:){0,4}[0-9a-f]{1,4})?::(?:[0-9a-f]{1,4}:[0-9a-f]{1,4}|(?:(?:0?0?[0-9]|0?[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])\.){3}(?:0?0?[0-9]|0?[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5]))|(?:(?:[0-9a-f]{1,4}:){0,5}[0-9a-f]{1,4})?::[0-9a-f]{1,4}|(?:(?:[0-9a-f]{1,4}:){0,6}[0-9a-f]{1,4})?::)|v[0-9a-f]+\.[a-z0-9-\._~!\$&'\(\)\*\+,;=:]+)\]|(?:(?:0?0?[0-9]|0?[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])\.){3}(?:0?0?[0-9]|0?[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])|[a-z0-9-\._~%0-9a-f!\$&'\(\)\*\+,;=]{0,255})(?::[0-9]*)?(?:\/[a-z0-9-\._~%0-9a-f!\$&'\(\)\*\+,;=:@]*)*)|\/(?:[a-z0-9-\._~%0-9a-f!\$&'\(\)\*\+,;=:@]+(?:\/[a-z0-9-\._~%0-9a-f!\$&'\(\)\*\+,;=:@]*)*)?|[a-z0-9-\._~%0-9a-f!\$&'\(\)\*\+,;=:@]+(?:\/[a-z0-9-\._~%0-9a-f!\$&'\(\)\*\+,;=:@]*)*))(?:\?[a-z0-9-\._~%0-9a-f!\$&'\(\)\*\+,;=:@\/\?]*(?=#|$))?(?:#[a-z0-9-\._~%0-9a-f!\$&'\(\)\*\+,;=:@\/\?]*)?$/, // eslint-disable-line
                            message:
                                '{label} must match the RFC 3986 specification (2 variations has been applied to the specification: the scheme can be omitted and capital letters are not allowed).',
                        };
                        break;
                    case 'custom':
                        if (typeof value !== 'function') {
                            throw new Error('custom constraint has to be a function');
                        } else {
                            this.customConstraints[name] = value;
                        }
                        break;
                    default:
                        normalized[name][constraint] = value;
                }
            });
        });
        return normalized;
    }

    @bind
    async _runWithContext(fn, data, context, inherited) {
        if (typeof fn === 'function') {
            return await fn(data, enrichContext(context), pick(inherited, ['data', 'index', 'path']));
        }
        if (typeof fn === 'string') {
            return await worker(await prepareFunction(fn), data, enrichContext(context), pick(inherited, ['data', 'index', 'path']));
        }
        return;
    }

    @bind
    async getHiddenOrDisabledorRequiredComponents(data, context, inherited) {
        const ignoreList = await Promise.all(
            Object.entries(this.components).map(async ([name, component]) => {
                // $FlowFixMe
                const isRequired = get(component, 'constraints.required');
                const constraints = get(component, 'constraints');
                const isVisible = get(component, 'properties.isVisible');
                const isDisabled = get(component, 'properties.disabled');

                let visible = true;
                if (isVisible) {
                    visible =
                        (typeof isVisible === 'function' && (await isVisible(data, context, inherited))) ||
                        (typeof isVisible === 'string' &&
                            (await worker(await prepareFunction(isVisible), data, enrichContext(context), pick(inherited, ['data', 'index', 'path'])))) ||
                        (typeof isVisible === 'boolean' && isVisible);
                }

                if (isRequired === false || (!isEmpty(constraints) && !isDefined(isRequired) && visible)) {
                    return false;
                }

                let required = false;
                if (isRequired) {
                    required =
                        (typeof isRequired === 'function' && (await isRequired(data, context, inherited))) ||
                        (typeof isRequired === 'string' &&
                            (await worker(await prepareFunction(isRequired), data, enrichContext(context), pick(inherited, ['data', 'index', 'path'])))) ||
                        (typeof isRequired === 'boolean' && isRequired);
                }

                const disabled =
                    (typeof isDisabled === 'function' && (await isDisabled(data, context, inherited))) ||
                    (typeof isDisabled === 'string' &&
                        (await worker(await prepareFunction(isDisabled), data, enrichContext(context), pick(inherited, ['data', 'index', 'path'])))) ||
                    (typeof isDisabled === 'boolean' && isDisabled);

                if (!visible || disabled || !required) {
                    return name;
                }

                return false;
            })
        );
        // $FlowFixMe
        return new Set(ignoreList.filter(Boolean));
    }

    @bind
    ignoreHidden(constraints, hidden /* Set */) {
        return Object.keys(constraints || {}).reduce((acc, key) => {
            if (!hidden.has(key)) {
                acc[key] = constraints[key];
            }
            return acc;
        }, {});
    }

    @bind
    @memoize()
    async getErrors(data, ctx = {}, inherited = {}) {
        await affectliSso.updateToken(5 /* minValidity in seconds */);
        const token = affectliSso.getToken();
        const context = set(ctx, 'sso.token', token);
        const noValidationComponents = await this.getHiddenOrDisabledorRequiredComponents(data, context, inherited);

        let constraints = this.ignoreHidden(this.constraints, noValidationComponents);
        if (!isEmpty(this.dynamicDefinitions)) {
            await Promise.all(
                Object.entries(this.dynamicDefinitions).map(async ([name, dynamicFn]) => {
                    const defs = await this._runWithContext(dynamicFn, data, context, inherited);
                    if (!isEmpty(defs)) {
                        const normalizedDefs = this._normalizeContraints({ [name]: defs });
                        constraints = {
                            ...constraints,
                            [name]: {
                                ...get(constraints, name, {}),
                                ...normalizedDefs[name],
                            },
                        };
                    }
                })
            );
        }
        const constraintsMap = {};
        await Promise.all([
            Object.entries(constraints).map(async ([name, constraint]) => {
                if (!noValidationComponents.has(name)) {
                    constraintsMap[name] = constraint || {};
                }
                if (this.customConstraints[name]) {
                    const res = await this._runWithContext(this.customConstraints[name], data, context, inherited);
                    constraintsMap[name] = { ...constraintsMap[name], ...(res || {}) };
                }
                return constraintsMap;
            }, {}),
        ]);
        constraints = constraintsMap;
        this.errors = validate(data || {}, constraints, { fullMessages: false });

        return this.errors;
    }

    async isValid(data, context, inherited) {
        const msgs = await this.getErrors(data, context, inherited);
        return isEmpty(msgs);
    }

    getComponents(name) {
        return this.components[name];
    }

    formatMessage(message, component) {
        const { properties } = component;
        const { name, title, label } = properties || {};
        return message.replace('{label}', label || title || name);
    }

    formatMessages(name, component) {
        const messages = this.errors && this.errors[name];
        if (!messages) {
            return null;
        }
        return messages.map(message => this.formatMessage(message, component));
    }

    getMessages() {
        if (!this.errors) {
            return null;
        }
        const messages = [];
        Object.keys(this.errors).forEach(name => {
            const component = this.getComponents(name);
            messages.push(...(this.formatMessages(name, component) || []));
        });
        return messages;
    }
}
